import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { type ContestModel } from '../../models/Contest';
import { type PoolModel } from '../../models/Pool';
import { type RoundModel } from '../../models/Round';
import {
  type ResolvedScoringConfig,
  type ResolvedScoringConfigMap,
  type RoleScoringConfigs,
} from '../../models/ScoringConfig';
import { type ScoringConfigTemplate } from '../../models/ScoringConfigTemplate';
import { type ContestRole } from '../../models/User';
import { buildHttpParams } from '../utils/http-params.util';

@Injectable({
  providedIn: 'root',
})
export class ScoringControllerService {
  // =============================================================================
  // Private Properties
  // =============================================================================

  private http = inject(HttpClient);
  private url = environment.apiUrl;

  // =============================================================================
  // Public Methods
  // =============================================================================

  // Template methods
  getTemplates(): Observable<ScoringConfigTemplate[]> {
    return this.http
      .get<{
        data: ScoringConfigTemplate[];
      }>(`${this.url}/scoring-configs/templates`)
      .pipe(map((response) => response.data));
  }

  // Pool methods
  getPoolScoringConfig(
    poolId: string,
    role?: ContestRole,
  ): Observable<ResolvedScoringConfig | ResolvedScoringConfigMap> {
    const params = buildHttpParams({ filters: { role: role as string } });

    return this.http
      .get<{
        data: ResolvedScoringConfig | ResolvedScoringConfigMap;
      }>(`${this.url}/pools/${poolId}/scoring-config`, { params })
      .pipe(map((response) => response.data));
  }

  deletePoolOverride(poolId: string): Observable<PoolModel> {
    return this.http
      .delete<{
        data: PoolModel;
      }>(`${this.url}/pools/${poolId}/scoring-config`)
      .pipe(map((res) => res.data));
  }

  setPoolScoringConfig(poolId: string, config: RoleScoringConfigs): Observable<void> {
    return this.http.put<void>(`${this.url}/pools/${poolId}/scoring-config`, {
      scoringConfig: config,
    });
  }

  // Round methods
  getRoundScoringConfig(
    roundId: string,
    role?: ContestRole,
  ): Observable<ResolvedScoringConfig | ResolvedScoringConfigMap> {
    const params = buildHttpParams({ filters: { role: role as string } });

    return this.http
      .get<{
        data: ResolvedScoringConfig | ResolvedScoringConfigMap;
      }>(`${this.url}/rounds/${roundId}/scoring-config`, { params })
      .pipe(map((response) => response.data));
  }

  deleteRoundOverride(roundId: string): Observable<RoundModel> {
    return this.http
      .delete<{
        data: RoundModel;
      }>(`${this.url}/rounds/${roundId}/scoring-config`)
      .pipe(map((res) => res.data));
  }

  setRoundScoringConfig(roundId: string, config: RoleScoringConfigs): Observable<void> {
    return this.http.put<void>(`${this.url}/rounds/${roundId}/scoring-config`, {
      scoringConfig: config,
    });
  }

  // Contest methods
  getContestScoringConfig(
    contestId: string,
    role?: ContestRole,
  ): Observable<ResolvedScoringConfig | ResolvedScoringConfigMap> {
    const params = buildHttpParams({ filters: { role: role as string } });

    return this.http
      .get<{
        data: ResolvedScoringConfig | ResolvedScoringConfigMap;
      }>(`${this.url}/contests/${contestId}/scoring-config`, { params })
      .pipe(map((response) => response.data));
  }

  deleteContestOverride(contestId: string): Observable<ContestModel> {
    return this.http
      .delete<{
        data: ContestModel;
      }>(`${this.url}/contests/${contestId}/scoring-config`)
      .pipe(map((res) => res.data));
  }

  setContestScoringConfig(contestId: string, config: RoleScoringConfigs): Observable<void> {
    return this.http.put<void>(`${this.url}/contests/${contestId}/scoring-config`, {
      scoringConfig: config,
    });
  }
}
